// Live Price Service - Simulates real-time price updates like Booking.com and Skyscanner

export interface PriceUpdate {
  id: string;
  type: 'flight' | 'hotel' | 'holiday';
  originalPrice: number;
  currentPrice: number;
  priceChange: number;
  percentageChange: number;
  lastUpdated: Date;
  trend: 'up' | 'down' | 'stable';
}

export interface LivePriceData {
  basePrice: number;
  currentPrice: number;
  lowestPrice: number;
  highestPrice: number;
  priceHistory: { timestamp: Date; price: number }[];
  lastUpdated: Date;
  volatility: 'low' | 'medium' | 'high';
}

class LivePriceService {
  private priceCache: Map<string, LivePriceData> = new Map();
  private updateIntervals: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Initialize live price tracking for an item
   */
  initializePriceTracking(itemId: string, basePrice: number, volatility: 'low' | 'medium' | 'high' = 'medium'): LivePriceData {
    const priceData: LivePriceData = {
      basePrice,
      currentPrice: basePrice,
      lowestPrice: basePrice,
      highestPrice: basePrice,
      priceHistory: [{ timestamp: new Date(), price: basePrice }],
      lastUpdated: new Date(),
      volatility,
    };

    this.priceCache.set(itemId, priceData);
    return priceData;
  }

  /**
   * Get current price data for an item
   */
  getPriceData(itemId: string): LivePriceData | null {
    return this.priceCache.get(itemId) || null;
  }

  /**
   * Simulate price fluctuation based on volatility
   */
  private calculatePriceChange(basePrice: number, volatility: 'low' | 'medium' | 'high'): number {
    const volatilityRanges = {
      low: { min: -0.02, max: 0.02 }, // ±2%
      medium: { min: -0.05, max: 0.05 }, // ±5%
      high: { min: -0.10, max: 0.10 }, // ±10%
    };

    const range = volatilityRanges[volatility];
    const changePercent = Math.random() * (range.max - range.min) + range.min;
    return Math.round(basePrice * changePercent);
  }

  /**
   * Update price for an item (simulates real-time market changes)
   */
  updatePrice(itemId: string): PriceUpdate | null {
    const priceData = this.priceCache.get(itemId);
    if (!priceData) return null;

    const priceChange = this.calculatePriceChange(priceData.basePrice, priceData.volatility);
    const newPrice = Math.max(Math.round(priceData.currentPrice + priceChange), Math.round(priceData.basePrice * 0.7)); // Never go below 70% of base

    const percentageChange = ((newPrice - priceData.currentPrice) / priceData.currentPrice) * 100;
    const trend: 'up' | 'down' | 'stable' = 
      percentageChange > 0.5 ? 'up' : 
      percentageChange < -0.5 ? 'down': 'stable';

    // Update price data
    priceData.currentPrice = newPrice;
    priceData.lowestPrice = Math.min(priceData.lowestPrice, newPrice);
    priceData.highestPrice = Math.max(priceData.highestPrice, newPrice);
    priceData.lastUpdated = new Date();
    priceData.priceHistory.push({ timestamp: new Date(), price: newPrice });

    // Keep only last 20 price points for memory optimization
    if (priceData.priceHistory.length > 20) {
      priceData.priceHistory = priceData.priceHistory.slice(-20);
    }

    this.priceCache.set(itemId, priceData);

    return {
      id: itemId,
      type: this.getItemType(itemId),
      originalPrice: priceData.basePrice,
      currentPrice: newPrice,
      priceChange,
      percentageChange,
      lastUpdated: new Date(),
      trend,
    };
  }

  /**
   * Start automatic price updates for an item
   */
  startLivePriceUpdates(itemId: string, callback: (update: PriceUpdate) => void, intervalMs: number = 15000): void {
    // Clear existing interval if any
    this.stopLivePriceUpdates(itemId);

    const interval = setInterval(() => {
      const update = this.updatePrice(itemId);
      if (update) {
        callback(update);
      }
    }, intervalMs);

    this.updateIntervals.set(itemId, interval);
  }

  /**
   * Stop automatic price updates for an item
   */
  stopLivePriceUpdates(itemId: string): void {
    const interval = this.updateIntervals.get(itemId);
    if (interval) {
      clearInterval(interval);
      this.updateIntervals.delete(itemId);
    }
  }

  /**
   * Get price trend indicator
   */
  getPriceTrend(itemId: string): 'up' | 'down' | 'stable' {
    const priceData = this.priceCache.get(itemId);
    if (!priceData || priceData.priceHistory.length < 2) return 'stable';

    const recentPrices = priceData.priceHistory.slice(-5);
    const avgRecent = recentPrices.reduce((sum, p) => sum + p.price, 0) / recentPrices.length;
    const current = priceData.currentPrice;

    const diff = ((current - avgRecent) / avgRecent) * 100;
    return diff > 1 ? 'up' : diff < -1 ? 'down' : 'stable';
  }

  /**
   * Get item type from ID
   */
  private getItemType(itemId: string): 'flight' | 'hotel' | 'holiday' {
    if (itemId.startsWith('flight-')) return 'flight';
    if (itemId.startsWith('hotel-')) return 'hotel';
    return 'holiday';
  }

  /**
   * Clear all price data and intervals
   */
  clearAll(): void {
    this.updateIntervals.forEach((interval) => clearInterval(interval));
    this.updateIntervals.clear();
    this.priceCache.clear();
  }
}

// Export singleton instance
export const livePriceService = new LivePriceService();
